import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Pencil, ExternalLink, RotateCcw, Loader2, ShieldCheck } from "lucide-react";

export default function LegalAdmin() {
  const [docs, setDocs] = useState([]);
  const [edit, setEdit] = useState(null);
  const [nuevaVersion, setNuevaVersion] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [consentimientos, setConsentimientos] = useState([]);

  const load = () => {
    api.get("/legal").then(({ data }) => setDocs(data));
    api.get("/consentimientos").then(({ data }) => setConsentimientos(data));
  };
  useEffect(() => { load(); }, []);

  const guardar = async () => {
    setGuardando(true);
    try {
      await api.put(`/legal/${edit.slug}`, { es: edit.es, en: edit.en, nueva_version: nuevaVersion });
      toast.success("Documento guardado");
      setEdit(null);
      setNuevaVersion(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setGuardando(false);
    }
  };

  const restaurar = async (slug) => {
    await api.post(`/legal/${slug}/reset`);
    toast.success("Texto original restaurado");
    setEdit(null);
    load();
  };

  const campos = (idioma) => (
    <div className="grid gap-4">
      <div className="grid gap-2">
        <Label>Título</Label>
        <Input value={edit[idioma].titulo} data-testid={`legal-titulo-${idioma}`}
          onChange={(e) => setEdit({ ...edit, [idioma]: { ...edit[idioma], titulo: e.target.value } })} />
      </div>
      <div className="grid gap-2">
        <Label>Contenido</Label>
        <Textarea rows={18} className="font-mono text-xs" value={edit[idioma].contenido}
          data-testid={`legal-contenido-${idioma}`}
          onChange={(e) => setEdit({ ...edit, [idioma]: { ...edit[idioma], contenido: e.target.value } })} />
        <p className="text-xs text-muted-foreground">
          Usa <code>## Título</code> para los apartados, <code>- </code> para listas y
          <code> **negrita** </code> para destacar. Separa los párrafos con una línea en blanco.
        </p>
      </div>
    </div>
  );

  return (
    <div data-testid="legal-admin-page">
      <PageHeader title="Textos legales"
        subtitle="Términos, privacidad y cookies que ven las familias, en español e inglés" />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {docs.map((d) => (
          <Card key={d.slug} className="rounded-2xl p-6 border-border shadow-sm" data-testid={`legal-${d.slug}`}>
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-display font-bold text-lg leading-tight">{d.es.titulo}</h3>
              <Badge variant={d.personalizado ? "secondary" : "outline"} className="rounded-full shrink-0">
                v{d.version}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-2">{d.en.titulo}</p>
            <p className="text-sm text-muted-foreground mt-4 line-clamp-3">
              {d.es.contenido.replace(/#/g, "").slice(0, 160)}…
            </p>
            <div className="flex gap-2 mt-6">
              <Button variant="outline" size="sm" className="rounded-lg" data-testid={`editar-legal-${d.slug}`}
                onClick={() => setEdit(JSON.parse(JSON.stringify(d)))}>
                <Pencil className="h-3.5 w-3.5 mr-1.5" /> Editar
              </Button>
              <a href={`/legal/${d.slug}`} target="_blank" rel="noreferrer">
                <Button variant="ghost" size="sm" className="rounded-lg" data-testid={`ver-legal-${d.slug}`}>
                  <ExternalLink className="h-3.5 w-3.5" />
                </Button>
              </a>
            </div>
          </Card>
        ))}
      </div>

      <h2 className="font-display text-2xl font-extrabold mt-12 mb-2">Registro de consentimientos</h2>
      <p className="text-sm text-muted-foreground mb-5">
        Prueba de aceptación de cada familia: versión del documento, fecha e IP (art. 7.1 RGPD).
      </p>
      <Card className="rounded-2xl border-border shadow-sm divide-y divide-border" data-testid="consentimientos-lista">
        {consentimientos.slice(0, 30).map((c) => (
          <div key={c.id} className="flex items-center justify-between gap-4 px-5 py-3.5 text-sm">
            <div className="min-w-0">
              <p className="font-semibold truncate">{c.familia || c.email}</p>
              <p className="text-xs text-muted-foreground truncate">
                Términos v{c.versiones?.terminos} · Privacidad v{c.versiones?.privacidad} ·
                {" "}{(c.fecha || "").slice(0, 16).replace("T", " ")} · IP {c.ip || "—"}
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              {c.imagenes && <Badge variant="secondary" className="rounded-full">Imágenes</Badge>}
              {c.comunicaciones && <Badge variant="secondary" className="rounded-full">Comunicaciones</Badge>}
              <ShieldCheck className="h-4 w-4 text-emerald-600" />
            </div>
          </div>
        ))}
        {consentimientos.length === 0 && (
          <p className="px-5 py-6 text-sm text-muted-foreground">
            Todavía no hay consentimientos registrados.
          </p>
        )}
      </Card>

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent className="rounded-2xl max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="legal-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{edit?.es.titulo}</DialogTitle>
          </DialogHeader>
          {edit && (
            <Tabs defaultValue="es">
              <TabsList>
                <TabsTrigger value="es" data-testid="tab-legal-es">Español</TabsTrigger>
                <TabsTrigger value="en" data-testid="tab-legal-en">English</TabsTrigger>
              </TabsList>
              <TabsContent value="es" className="mt-5">{campos("es")}</TabsContent>
              <TabsContent value="en" className="mt-5">{campos("en")}</TabsContent>
            </Tabs>
          )}
          <div className="flex items-center justify-between rounded-lg border border-border p-3 mt-2">
            <div>
              <Label>Publicar como versión nueva</Label>
              <p className="text-xs text-muted-foreground mt-1">
                Sube el número de versión: las familias que acepten a partir de ahora quedarán registradas con ella.
              </p>
            </div>
            <Switch checked={nuevaVersion} onCheckedChange={setNuevaVersion} data-testid="legal-nueva-version-switch" />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" className="rounded-full" onClick={() => restaurar(edit.slug)}
              data-testid="restaurar-legal-btn">
              <RotateCcw className="h-4 w-4 mr-2" /> Restaurar original
            </Button>
            <Button className="rounded-full font-semibold" onClick={guardar} disabled={guardando}
              data-testid="guardar-legal-btn">
              {guardando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
